/* Decotimber · Lote 5: request forms for the quote page (T13, <dt-quote-form>) and the made-to-order page
   (T09, <dt-custom-form>). Both only PREPARE a WhatsApp message (wa.me link + copy): nothing is sent, stored or
   uploaded by the page, and the cart is only read (GET cart.js) — the quote list is an editable copy.
   Cart groups follow the theme's rule (snippets/dt-cart-groups.liquid): a group is the _dt_tramo value (run + its
   end post) or the line key. Units follow dt-cart.js: M² → m², ML → ml, anything else → unidad. */
(() => {
 'use strict';
 if (window.DecotimberRequest) return;
 const MAX_AUTO_URL = 1800; // Same conservative threshold as the cart's advance message.
 const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
 const fill = (template, data) => String(template || '').replace(/\[(\w+)\]/g, (m, k) => data[k] ?? m);
 const json = node => { try { return JSON.parse(node?.textContent || '{}'); } catch { return {}; } };
 const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
 const ML = /^(ml|m\.l\.|metros? lineal(es)?|por metro lineal)$/i, M2 = /^(m²|m2|metros? cuadrados?|por m²)$/i;
 const UNIT_VALUE = /^(ml|m\.l\.|metros? lineal(es)?|por metro lineal|m²|m2|metros? cuadrados?|por m²|unidad|unidades|por unidad|ud|default title)$/i;
 const unitOf = values => { const v = values.map(clean); return v.some(x => ML.test(x)) ? 'ml' : v.some(x => M2.test(x)) ? 'm²' : 'ud'; };
 const isUnitValue = value => UNIT_VALUE.test(clean(value));
 // «12,5» and «1.200» (thousands) are read as numbers; anything else is NaN. Empty → null.
 function parseNumber(value) {
  let s = clean(value).replace(/\s/g, '');
  if (!s) return null;
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, '');
  s = s.replace(',', '.');
  return /^\d*\.?\d+$/.test(s) ? Number(s) : NaN;
 }
 const formatNumber = n => Number(n).toLocaleString('es-CL', { maximumFractionDigits: 2 });
 const waURL = (number, text) => `https://wa.me/${number}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
 const absolute = url => { try { return url ? new URL(url, window.location.origin).href : ''; } catch { return ''; } };
 const phoneOK = value => { const digits = clean(value).replace(/\D/g, ''); return /^[+\d\s().-]+$/.test(clean(value)) && digits.length >= 8 && digits.length <= 15; };
 const emailOK = value => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean(value));
 const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
 let uid = 0;

 class RequestForm extends HTMLElement {
  connectedCallback() {
   if (this.ready) return; this.ready = true;
   this.t = json(this.querySelector('[data-dt-req-text]'));
   this.form = this.querySelector('[data-dt-form]');
   this.tried = false;
   this.form.addEventListener('submit', event => { event.preventDefault(); this.prepare(true); });
   this.querySelector('[data-dt-preview]')?.addEventListener('click', () => this.prepare(false));
   this.querySelector('[data-dt-result-copy]')?.addEventListener('click', () => this.copy());
   this.form.addEventListener('input', () => this.changed());
   this.form.addEventListener('change', () => this.changed());
   this.init();
   this.changed();
  }
  init() {}
  changed() { if (this.tried) this.validate(false); }
  value(name) { const f = this.form.elements[name]; if (!f) return ''; if (f instanceof RadioNodeList) return clean(f.value); return f.type === 'checkbox' ? (f.checked ? f.value : '') : clean(f.value); }
  unitLabel(unit, qty) { return unit === 'm²' ? this.t.unit_m2 : unit === 'ml' ? this.t.unit_ml : qty === 1 ? this.t.unit_ud_one : this.t.unit_ud_other; }
  quantity(qty, unit) { return qty == null ? '' : `${formatNumber(qty)} ${this.unitLabel(unit, qty)}`; }
  // Shows (or clears) the inline error of a field wrapper; returns the control to focus when invalid.
  setError(name, message) {
   const wrap = this.form.querySelector(`[data-dt-field="${name}"]`); if (!wrap) return null;
   const box = wrap.querySelector(':scope > .dt-req-error'), control = wrap.dataset.dtFocus ? this.form.querySelector(wrap.dataset.dtFocus) : wrap.querySelector('input, select, textarea');
   if (box) { box.textContent = message || ''; box.hidden = !message; }
   wrap.classList.toggle('is-invalid', !!message);
   if (control && !wrap.matches('fieldset') && !wrap.dataset.dtFocus) { if (message) control.setAttribute('aria-invalid', 'true'); else control.removeAttribute('aria-invalid'); }
   return message ? control || wrap : null;
  }
  contactErrors() {
   return [
    ['name', this.value('name') ? '' : this.t.err_name],
    ['phone', phoneOK(this.value('phone')) ? '' : this.t.err_phone],
    ['email', !this.value('email') || emailOK(this.value('email')) ? '' : this.t.err_email]
   ];
  }
  // errors: [[fieldName, message], ...] in reading order.
  validate(focus) {
   const errors = this.errors(), first = errors.map(([name, message]) => this.setError(name, message)).find(Boolean);
   const summary = this.querySelector('[data-dt-error-summary]');
   if (summary) { summary.textContent = first ? this.t.err_summary : ''; summary.hidden = !first; }
   if (first && focus) { first.focus({ preventScroll: true }); first.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
   return !first;
  }
  // Builds the message; opens WhatsApp only from a submit (a user gesture). Never says «sent», never clears anything.
  prepare(open) {
   this.tried = true;
   if (!this.validate(true)) return;
   const message = this.message(), full = waURL(this.dataset.wa, message), long = full.length > MAX_AUTO_URL;
   const result = this.querySelector('[data-dt-result]'), link = result.querySelector('[data-dt-result-open]'), details = result.querySelector('[data-dt-result-details]');
   result.querySelector('[data-dt-result-message]').value = message;
   link.href = long ? waURL(this.dataset.wa, '') : full;
   result.querySelector('[data-dt-result-open-label]').textContent = long ? this.t.open_plain : this.t.open;
   details.open = long;
   result.hidden = false;
   // window.open with noopener returns null even when it worked, so the status text covers a blocked pop-up too.
   if (open && !long) { try { window.open(full, '_blank', 'noopener'); } catch { /* The explicit link remains. */ } }
   result.querySelector('[data-dt-result-status]').textContent = long ? this.t.long : open ? this.t.opened : this.t.ready;
   if (!open || long) (long ? details.querySelector('summary') : link).focus();
  }
  async copy() {
   const area = this.querySelector('[data-dt-result-message]'), status = this.querySelector('[data-dt-result-status]');
   try { if (!navigator.clipboard?.writeText) throw new Error('clipboard'); await navigator.clipboard.writeText(area.value); status.textContent = this.t.copied; }
   catch { area.focus(); area.select(); status.textContent = this.t.copy_manual; }
  }
  section(title, lines) { const body = lines.filter(Boolean); return body.length ? [`*${title}*`, ...body].join('\n') : ''; }
  line(label, value) { return clean(value) ? `${label}: ${clean(value)}` : ''; }
  contactLines(withProfile) {
   return [withProfile ? this.line(this.t.m_profile, this.value('profile')) : '', this.line(this.t.m_name, this.value('name')), this.line(this.t.m_phone, this.value('phone')),
    this.line(this.t.m_company, this.value('company')), this.line(this.t.m_email, this.value('email'))];
  }
 }

 // ---------- T13 · Cotización ----------
 class QuoteForm extends RequestForm {
  init() {
   this.items = [];
   this.list = this.querySelector('[data-dt-items]');
   this.cartBox = this.querySelector('[data-dt-use-cart]');
   this.listStatus = this.querySelector('[data-dt-list-status]');
   this.cartSeq = 0;
   this.cartBox.addEventListener('change', () => this.toggleCart());
   this.list.addEventListener('click', event => { const b = event.target.closest('[data-dt-remove]'); if (b) this.remove(b.dataset.dtRemove); });
   this.list.addEventListener('input', event => { const i = event.target.closest('[data-dt-qty]'); if (i) this.editQty(i); });
   this.setupAdder();
   const params = new URLSearchParams(window.location.search);
   // ?perfil=constructora|arquitecto only pre-selects a chip on this visit: the customer can change it and nothing is stored.
   const profile = { constructora: 'builder', arquitecto: 'architect' }[params.get('perfil')];
   const chip = profile && this.querySelector(`[data-dt-profile="${profile}"]`);
   if (chip && !this.form.querySelector('[name="profile"]:checked')) chip.checked = true;
   this.setupSteps();
   const wanted = this.cartBox.checked || params.has('carrito');
   this.cartBox.checked = wanted;
   if (wanted) this.toggleCart(); else this.render();
   this.form.addEventListener('change', event => { if (event.target.name === 'delivery') this.form.classList.toggle('is-shipping', event.target.value === 'ship'); });
   this.form.classList.toggle('is-shipping', this.value('delivery') === 'ship');
   this.stickySummary();
  }
  // Reads the cart once per activation and replaces the previous cart copy: never duplicates groups and never writes.
  async toggleCart() {
   const seq = ++this.cartSeq;
   this.items = this.items.filter(item => item.source !== 'cart');
   if (!this.cartBox.checked) { this.render(); this.listStatus.textContent = this.t.cart_off; return; }
   this.listStatus.textContent = this.t.cart_loading;
   try {
    const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const cart = await response.json();
    if (seq !== this.cartSeq || !this.cartBox.checked) return;
    const groups = this.groupsFromCart(cart);
    this.items = [...groups, ...this.items.filter(item => item.source !== 'cart')];
    this.listStatus.textContent = groups.length ? fill(this.t.cart_loaded, { n: this.configs(groups.length) }) : this.t.cart_empty;
   } catch (error) {
    if (seq === this.cartSeq) this.listStatus.textContent = this.t.cart_error;
    console.error('[Decotimber quote cart]', error);
   }
   this.render();
  }
  configs(n) { return n === 1 ? this.t.config_one : fill(this.t.config_other, { n }); }
  groupsFromCart(cart) {
   const groups = new Map();
   (cart?.items || []).forEach(item => { const key = clean(item.properties?._dt_tramo) || item.key; if (!groups.has(key)) groups.set(key, []); groups.get(key).push(item); });
   return [...groups.entries()].map(([key, lines]) => {
    lines.sort((a, b) => (a.properties?._dt_rol === 'poste') - (b.properties?._dt_rol === 'poste'));
    const run = !!clean(lines[0].properties?._dt_tramo);
    return { id: `cart:${key}`, source: 'cart', run, lines: lines.map(item => this.lineFromCart(item)) };
   });
  }
  lineFromCart(item) {
   const options = (item.options_with_values || []).map(o => ({ name: clean(o.name), value: clean(o.value) })).filter(o => o.value && !/^default title$/i.test(o.value));
   const values = options.length ? options.map(o => o.value) : [clean(item.variant_title)];
   const props = Object.entries(item.properties || {}).filter(([k, v]) => clean(k) && !clean(k).startsWith('_') && clean(v)).map(([k, v]) => ({ name: clean(k), value: clean(v) }));
   return { title: clean(item.product_title || item.title), url: item.url || '', unit: unitOf(values), qty: Number(item.quantity),
    options: options.filter(o => !isUnitValue(o.value)), props, variant: item.variant_id, post: item.properties?._dt_rol === 'poste' };
  }
  remove(id) {
   const item = this.items.find(x => x.id === id); if (!item) return;
   const row = this.list.querySelector(`[data-id="${CSS.escape(id)}"]`), next = row?.nextElementSibling?.querySelector('[data-dt-remove]') || row?.previousElementSibling?.querySelector('[data-dt-remove]');
   this.items = this.items.filter(x => x !== item);
   this.render();
   this.listStatus.textContent = fill(this.t.removed, { name: item.lines[0].title });
   (next ? this.list.querySelector(`[data-dt-remove="${CSS.escape(next.dataset.dtRemove)}"]`) : this.querySelector('[data-dt-adder-toggle]'))?.focus();
  }
  editQty(input) {
   const item = this.items.find(x => x.id === input.dataset.dtQty); if (!item) return;
   const n = parseNumber(input.value); item.lines[0].qty = n; item.lines[0].raw = input.value;
   this.changed(); this.updateSummary();
  }
  qtyError(item) {
   if (item.run) return '';
   const n = item.lines[0].qty;
   if (item.source === 'manual') return n == null || n > 0 ? '' : this.t.err_positive;
   return Number.isInteger(n) && n > 0 ? '' : this.t.err_whole;
  }
  errors() {
   const errs = [];
   const itemErrors = this.items.map(item => [item, this.qtyError(item)]);
   itemErrors.forEach(([item, message]) => {
    const row = this.list.querySelector(`[data-id="${CSS.escape(item.id)}"]`); if (!row) return;
    const box = row.querySelector('.dt-req-error'), input = row.querySelector('[data-dt-qty]');
    box.textContent = message; box.hidden = !message; row.classList.toggle('is-invalid', !!message);
    if (input) { if (message) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
   });
   const badRow = itemErrors.find(([, m]) => m);
   errs.push(['items', this.items.length || this.value('description') ? '' : this.t.err_items]);
   const delivery = this.value('delivery');
   errs.push(['delivery', delivery ? '' : this.t.err_delivery]);
   errs.push(['commune', delivery !== 'ship' || this.value('commune') ? '' : this.t.err_commune]);
   errs.push(...this.contactErrors());
   const first = errs.find(([, m]) => m);
   // A bad quantity is shown on its row; it goes first because it sits first on the page.
   if (badRow) return [['__row', badRow[0].id], ...errs];
   return first ? errs : [];
  }
  validate(focus) {
   const errors = this.errors(), rowId = errors[0]?.[0] === '__row' ? errors[0][1] : null;
   const fields = errors.filter(([n]) => n !== '__row');
   const firstField = fields.map(([name, message]) => this.setError(name, message)).find(Boolean);
   if (!errors.length) ['items', 'delivery', 'commune', 'name', 'phone', 'email'].forEach(n => this.setError(n, ''));
   const first = rowId ? this.list.querySelector(`[data-id="${CSS.escape(rowId)}"] [data-dt-qty]`) : firstField;
   const summary = this.querySelector('[data-dt-error-summary]');
   summary.textContent = first ? this.t.err_summary : ''; summary.hidden = !first;
   if (first && focus) { first.focus({ preventScroll: true }); first.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
   return !first;
  }
  changed() { super.changed(); this.updateSummary(); this.updateSteps(); }
  // ----- list rendering (DOM order = reading order; no reordering tricks) -----
  render() {
   this.list.replaceChildren(...this.items.map(item => this.row(item)));
   this.querySelector('[data-dt-items-empty]').hidden = this.items.length > 0;
   if (this.tried) this.validate(false);
   this.updateSummary(); this.updateSteps();
  }
  row(item) {
   const main = item.lines[0], li = el('li', 'dt-req-item'); li.dataset.id = item.id;
   const info = el('div', 'dt-req-item__info'), title = el('p', 'dt-req-item__title');
   if (main.url) { const a = el('a', null, main.title); a.href = main.url; title.append(a); } else title.append(main.title);
   const badge = item.source === 'cart' ? this.t.from_cart : item.source === 'manual' ? this.t.manual_badge : '';
   if (badge) title.append(' ', el('span', 'dt-req-badge', badge));
   info.append(title);
   const detail = el('div', 'dt-req-item__detail');
   const parts = [...main.options.map(o => o.value), ...main.props.map(p => `${p.name}: ${p.value}`)];
   if (main.note) parts.push(main.note);
   if (parts.length) detail.append(el('p', null, parts.join(' · ')));
   item.lines.slice(1).forEach(line => detail.append(el('p', 'dt-req-item__sub', `↳ ${line.title}${line.options.length ? ` · ${line.options.map(o => o.value).join(' · ')}` : ''} · ${this.quantity(line.qty, line.unit)}`)));
   if (item.run) detail.append(el('p', 'dt-req-item__note', this.t.fixed_qty));
   else if (main.unit === 'ml' && item.source !== 'manual') detail.append(el('p', 'dt-req-item__note', this.t.ml_note));
   const qty = el('div', 'dt-req-item__qty');
   if (item.run) qty.append(el('span', 'dt-req-item__fixed', this.quantity(main.qty, main.unit)));
   else {
    const id = `dt-q-${item.id.replace(/[^\w-]/g, '-')}`, label = el('label', 'visually-hidden', fill(this.t.qty_of, { name: main.title, unit: this.unitLabel(main.unit, 2) }));
    const input = el('input', 'dt-req-input dt-req-input--qty'); label.htmlFor = input.id = id;
    input.inputMode = item.source === 'manual' ? 'decimal' : 'numeric'; input.autocomplete = 'off';
    input.value = main.raw ?? (main.qty == null ? '' : String(main.qty).replace('.', ',')); input.dataset.dtQty = item.id;
    input.setAttribute('aria-describedby', `${id}-error`);
    qty.append(label, input, el('span', 'dt-req-item__unit', this.unitLabel(main.unit, main.qty)));
   }
   const remove = el('button', 'dt-req-item__remove', this.t.remove); remove.type = 'button'; remove.dataset.dtRemove = item.id;
   remove.setAttribute('aria-label', fill(this.t.remove_label, { name: main.title }));
   const error = el('p', 'dt-req-error'); error.hidden = true; error.id = `dt-q-${item.id.replace(/[^\w-]/g, '-')}-error`;
   li.append(info, detail, qty, remove, error);
   return li;
  }
  // ----- add from the catalog (predictive search + /products/handle.js) or as a manual line -----
  setupAdder() {
   const toggle = this.querySelector('[data-dt-adder-toggle]'), panel = this.querySelector('.dt-req-adder__panel');
   this.search = this.querySelector('[data-dt-search]'); this.results = this.querySelector('[data-dt-results]'); this.picker = this.querySelector('[data-dt-picker]');
   this.searchStatus = this.querySelector('[data-dt-search-status]');
   toggle.addEventListener('click', () => { const open = panel.hidden; panel.hidden = !open; toggle.setAttribute('aria-expanded', String(open)); if (open) this.search.focus(); });
   let timer;
   this.search.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => this.find(this.search.value), 280); });
   this.search.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); clearTimeout(timer); this.find(this.search.value); } });
   // Enter inside the adder adds the line instead of submitting the whole request.
   this.querySelector('[data-dt-adder]').addEventListener('keydown', event => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement) || event.target === this.search) return;
    event.preventDefault(); event.target.closest('[data-dt-manual], [data-dt-picker]')?.querySelector('.dt-req-button--dark')?.click();
   });
   this.results.addEventListener('click', event => { const b = event.target.closest('[data-handle]'); if (b) this.pick(b.dataset.handle); });
   const manualToggle = this.querySelector('[data-dt-manual-toggle]'), manual = this.querySelector('[data-dt-manual]');
   const closeManual = () => { manual.hidden = true; manualToggle.setAttribute('aria-expanded', 'false'); };
   manualToggle.addEventListener('click', () => { const open = manual.hidden; manual.hidden = !open; manualToggle.setAttribute('aria-expanded', String(open)); if (open) manual.querySelector('input').focus(); });
   this.querySelector('[data-dt-manual-cancel]').addEventListener('click', () => { closeManual(); manualToggle.focus(); });
   this.querySelector('[data-dt-manual-add]').addEventListener('click', () => {
    const name = this.value('manual_name'), qty = parseNumber(this.value('manual_qty'));
    const nameError = this.setError('manual_name', name ? '' : this.t.err_manual), qtyError = this.setError('manual_qty', qty == null || qty > 0 ? '' : this.t.err_positive);
    if (nameError || qtyError) { (nameError || qtyError).focus(); return; }
    this.items.push({ id: `manual:${++uid}`, source: 'manual', run: false, lines: [{ title: name, url: '', unit: this.value('manual_unit') || 'ud', qty, raw: this.value('manual_qty'), options: [], props: [], note: this.value('manual_note') }] });
    ['manual_name', 'manual_qty', 'manual_note'].forEach(n => { this.form.elements[n].value = ''; });
    closeManual(); this.render(); this.listStatus.textContent = fill(this.t.added, { name }); manualToggle.focus();
   });
  }
  async find(query) {
   const q = clean(query); this.picker.hidden = true;
   if (q.length < 2) { this.results.replaceChildren(); this.searchStatus.textContent = ''; return; }
   this.abort?.abort(); this.abort = new AbortController();
   this.searchStatus.textContent = this.t.searching;
   try {
    const params = new URLSearchParams({ q, 'resources[type]': 'product', 'resources[limit]': '8', 'resources[options][unavailable_products]': 'last' });
    const response = await fetch(`${root()}search/suggest.json?${params}`, { signal: this.abort.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const products = (await response.json())?.resources?.results?.products || [];
    this.results.replaceChildren(...products.map(p => { const li = el('li'), b = el('button', 'dt-req-result-item'); b.type = 'button'; b.dataset.handle = p.handle; b.append(el('span', null, clean(p.title)), el('span', 'dt-req-result-item__cta', this.t.choose)); li.append(b); return li; }));
    this.searchStatus.textContent = products.length ? '' : this.t.no_results;
   } catch (error) { if (error.name !== 'AbortError') { this.searchStatus.textContent = this.t.search_error; console.error('[Decotimber quote search]', error); } }
  }
  async pick(handle) {
   this.searchStatus.textContent = this.t.searching;
   try {
    const response = await fetch(`${root()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    this.showPicker(await response.json());
    this.searchStatus.textContent = '';
   } catch (error) { this.searchStatus.textContent = this.t.search_error; console.error('[Decotimber quote product]', error); }
  }
  // Real option selectors from the product; the sale unit comes from the selected option values (never the title).
  showPicker(product) {
   const options = (product.options || []).map(o => ({ name: clean(o.name ?? o), values: (o.values || []).map(clean) })).filter(o => o.values.length && !(o.values.length === 1 && /^default title$/i.test(o.values[0])));
   const variants = product.variants || [], complete = variants.length < 250;
   const box = el('div', 'dt-req-picker__box'), heading = el('p', 'dt-req-picker__title', clean(product.title)), selects = [];
   box.append(heading);
   const grid = el('div', 'dt-req-row dt-req-row--3');
   options.forEach((option, i) => {
    const wrap = el('div', 'dt-req-field'), id = `dt-pick-${++uid}`, label = el('label', 'dt-req-label', option.name), select = el('select', 'dt-req-input');
    label.htmlFor = select.id = id; option.values.forEach(v => { const o = el('option', null, v); o.value = v; select.append(o); });
    const first = variants.find(v => v.available) || variants[0]; if (first?.options?.[i]) select.value = first.options[i];
    selects.push(select); wrap.append(label, select); grid.append(wrap);
   });
   const qtyWrap = el('div', 'dt-req-field'), qtyId = `dt-pick-${++uid}`, qtyLabel = el('label', 'dt-req-label'), qty = el('input', 'dt-req-input'), qtyErr = el('p', 'dt-req-error');
   qtyLabel.htmlFor = qty.id = qtyId; qty.inputMode = 'numeric'; qty.autocomplete = 'off'; qtyErr.hidden = true; qtyErr.id = `${qtyId}-error`; qty.setAttribute('aria-describedby', qtyErr.id);
   qtyWrap.append(qtyLabel, qty, qtyErr); grid.append(qtyWrap); box.append(grid);
   const note = el('p', 'dt-req-hint'); note.hidden = true; box.append(note);
   if (!complete) box.append(el('p', 'dt-req-hint', this.t.partial_variants));
   const current = () => { const values = selects.map(s => s.value); return { values, unit: unitOf(values), variant: variants.find(v => (v.options || []).every((x, i) => clean(x) === values[i])) }; };
   const sync = () => { const c = current(); qtyLabel.textContent = `${this.t.qty} (${this.unitLabel(c.unit, 2)})`; const missing = complete && !c.variant; note.textContent = missing ? this.t.unavailable : c.unit === 'ml' ? this.t.ml_note : ''; note.hidden = !note.textContent; add.disabled = missing; };
   const actions = el('div', 'dt-req-actions'), add = el('button', 'dt-req-button dt-req-button--dark', this.t.add_to_request), cancel = el('button', 'dt-req-button dt-req-button--outline', this.t.cancel);
   add.type = cancel.type = 'button'; actions.append(add, cancel); box.append(actions);
   selects.forEach(s => s.addEventListener('change', sync)); sync();
   cancel.addEventListener('click', () => { this.picker.hidden = true; this.search.focus(); });
   add.addEventListener('click', () => {
    const n = parseNumber(qty.value), c = current();
    if (!(Number.isInteger(n) && n > 0)) { qtyErr.textContent = this.t.err_whole; qtyErr.hidden = false; qty.setAttribute('aria-invalid', 'true'); qty.focus(); return; }
    const named = options.map((o, i) => ({ name: o.name, value: c.values[i] })).filter(o => !isUnitValue(o.value));
    const key = c.variant ? `v${c.variant.id}` : `${product.handle}|${c.values.join('|')}`, title = clean(product.title);
    const same = this.items.find(x => x.source === 'catalog' && x.key === key);
    if (same) { same.lines[0].qty = (Number.isFinite(same.lines[0].qty) ? same.lines[0].qty : 0) + n; same.lines[0].raw = String(same.lines[0].qty); }
    else this.items.push({ id: `catalog:${++uid}`, key, source: 'catalog', run: false, lines: [{ title, url: c.variant ? `${product.url || `${root()}products/${product.handle}`}?variant=${c.variant.id}` : (product.url || `${root()}products/${product.handle}`), unit: c.unit, qty: n, options: named, props: [] }] });
    this.picker.hidden = true; this.results.replaceChildren(); this.search.value = '';
    this.render(); this.listStatus.textContent = fill(same ? this.t.merged : this.t.added, { name: title }); this.search.focus();
   });
   this.picker.replaceChildren(box); this.picker.hidden = false; (selects[0] || qty).focus();
  }
  // ----- summary and step indicator -----
  updateSummary() {
   const line = this.querySelector('[data-dt-summary-line]'); if (!line) return;
   const delivery = this.value('delivery'), commune = delivery === 'ship' ? this.value('commune') : '';
   const what = this.items.length ? this.configs(this.items.length) : this.value('description') ? this.t.summary_project : this.t.summary_none;
   line.textContent = [what, delivery === 'pickup' ? this.t.pickup_short : delivery === 'ship' ? this.t.ship_short : '', commune].filter(Boolean).join(' · ');
  }
  setupSteps() {
   this.steps = [...this.querySelectorAll('[data-dt-step]')];
   this.steps.forEach(a => a.addEventListener('click', event => {
    const target = document.getElementById(a.getAttribute('href').slice(1)); if (!target) return;
    event.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); target.focus({ preventScroll: true });
   }));
  }
  // Completion is computed from the real field values; step 4 is never shown as done (the page cannot know it was sent).
  updateSteps() {
   if (!this.steps) return;
   const delivery = this.value('delivery');
   const done = [
    (this.items.length > 0 && this.items.every(item => !this.qtyError(item))) || (!this.items.length && !!this.value('description')),
    delivery === 'pickup' || (delivery === 'ship' && !!this.value('commune')),
    this.contactErrors().every(([, m]) => !m),
    false
   ];
   const current = done.findIndex(d => !d);
   this.steps.forEach((a, i) => {
    a.classList.toggle('is-done', done[i]); a.classList.toggle('is-current', i === current);
    if (i === current) a.setAttribute('aria-current', 'step'); else a.removeAttribute('aria-current');
    a.querySelector('[data-dt-step-state]').textContent = done[i] ? ` (${this.t.step_done})` : '';
   });
   const names = [this.t.step_products, this.t.step_delivery, this.t.step_data, this.t.step_send];
   this.querySelector('[data-dt-step-label]').textContent = fill(this.t.step_of, { n: current + 1, name: names[current] });
   this.querySelector('[data-dt-step-bar]').style.width = `${(done.filter(Boolean).length / 4) * 100}%`;
  }
  // Desktop: the summary sticks only while it fits in the viewport, so it never hides content.
  stickySummary() {
   const aside = this.querySelector('[data-dt-summary]'); if (!aside) return;
   const check = () => aside.classList.toggle('is-sticky', window.matchMedia('(min-width: 990px)').matches && aside.offsetHeight + 120 < window.innerHeight);
   check(); window.addEventListener('resize', check); if (window.ResizeObserver) new ResizeObserver(check).observe(aside);
  }
  message() {
   const t = this.t, lines = this.items.map((item, i) => {
    const main = item.lines[0], out = [`${i + 1}. ${item.source === 'manual' ? `${t.m_manual}: ` : ''}${main.title}`];
    main.options.forEach(o => out.push(`   ${o.name}: ${o.value}`));
    main.props.forEach(p => out.push(`   ${p.name}: ${p.value}`));
    if (main.note) out.push(`   ${t.m_note}: ${main.note}`);
    if (main.qty != null && Number.isFinite(main.qty)) out.push(`   ${t.m_qty}: ${this.quantity(main.qty, main.unit)}`);
    item.lines.slice(1).forEach(line => out.push(`   + ${line.title}${line.options.length ? ` (${line.options.map(o => o.value).join(', ')})` : ''}: ${this.quantity(line.qty, line.unit)}`));
    const url = absolute(main.url); if (url) out.push(`   ${url}`);
    return out.join('\n');
   });
   const delivery = this.value('delivery');
   return [
    this.dataset.intro,
    lines.length ? `*${t.m_products} (${this.configs(lines.length)})*\n${lines.join('\n')}` : '',
    this.section(t.m_project, [this.value('description'), this.value('files') ? t.m_files_quote : '']),
    this.section(t.m_delivery, [delivery === 'ship' ? t.m_ship : t.m_pickup, delivery === 'ship' ? this.line(t.m_commune, this.value('commune')) : '', delivery === 'ship' ? this.line(t.m_region, this.value('region')) : '', this.line(t.m_date, this.value('date'))]),
    this.section(t.m_data, this.contactLines(true))
   ].filter(Boolean).join('\n\n');
  }
 }

 // ---------- T09 · Piezas a medida ----------
 class CustomForm extends RequestForm {
  errors() {
   const errs = [['piece', this.value('piece') || this.value('comments') ? '' : this.t.err_piece]];
   ['size_l', 'size_w', 'size_t', 'qty'].forEach(name => { const n = parseNumber(this.value(name)); errs.push([name, n == null || n > 0 ? '' : this.t.err_positive]); });
   errs.push(...this.contactErrors().filter(([n]) => n !== 'email'));
   return errs.some(([, m]) => m) ? errs : errs.map(([n]) => [n, '']);
  }
  message() {
   const t = this.t, cm = name => { const n = parseNumber(this.value(name)); return n ? `${formatNumber(n)} cm` : ''; };
   const size = [['size_l', t.length], ['size_w', t.width], ['size_t', t.thickness]].map(([n, label]) => cm(n) ? `${label} ${cm(n)}` : '').filter(Boolean).join(' × ');
   const qty = parseNumber(this.value('qty')), unit = this.value('unit');
   const amount = qty ? (unit ? this.quantity(qty, unit) : formatNumber(qty)) : unit ? this.unitLabel(unit, 2) : '';
   return [
    this.dataset.intro,
    this.section(t.m_piece, [this.line(t.m_type, this.value('piece')), this.line(t.m_finish, this.value('finish')), this.line(t.m_size, size),
     this.line(t.m_qty, amount), this.line(t.m_use, [this.value('place'), this.value('traffic')].filter(Boolean).join(' · '))]),
    this.section(t.m_site, [this.line(t.m_commune, this.value('commune')), this.line(t.m_date, this.value('date'))]),
    this.section(t.m_comments, [this.value('comments')]),
    this.value('files') ? t.m_files_custom : '',
    this.section(t.m_data, this.contactLines(false))
   ].filter(Boolean).join('\n\n');
  }
 }

 if (!customElements.get('dt-quote-form')) customElements.define('dt-quote-form', QuoteForm);
 if (!customElements.get('dt-custom-form')) customElements.define('dt-custom-form', CustomForm);
 window.DecotimberRequest = Object.freeze({ unitOf, parseNumber, waURL, MAX_AUTO_URL });
})();
